import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { promoteLeadToProspect } from '@/services/prospectsApi';

interface Props {
  leadId: string;
  onPromoted?: () => void;
  showTitle?: boolean;
}

export default function PromoteToProspectPanel({ leadId, onPromoted, showTitle = true }: Props) {
  const [mode, setMode] = useState<'pdf' | 'url'>('pdf');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const promote = useMutation({
    mutationFn: (opts: { file?: File; linkUrl?: string }) =>
      promoteLeadToProspect(leadId, opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['prospects'] });
      setFile(null);
      setLinkUrl('');
      onPromoted?.();
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      if (status === 403) {
        alert("You don't have permission to promote leads.");
      } else {
        alert(err?.response?.data?.errors?.[0] ?? 'Failed to promote lead. Please try again.');
      }
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const selected = e.target.files?.[0] ?? null;
    if (!selected) { setFile(null); return; }

    if (selected.type !== 'application/pdf') {
      setFileError('Only PDF files are accepted.');
      setFile(null);
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setFileError('File must be 10 MB or smaller.');
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'pdf') {
      if (!file) return;
      promote.mutate({ file });
    } else {
      if (!linkUrl.trim()) { setUrlError('Please enter a URL.'); return; }
      try { new URL(linkUrl.trim()); } catch {
        setUrlError('Please enter a valid URL (e.g. https://example.com).');
        return;
      }
      setUrlError(null);
      promote.mutate({ linkUrl: linkUrl.trim() });
    }
  };

  const isSubmitDisabled =
    promote.isPending || (mode === 'pdf' ? !file : !linkUrl.trim());

  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface-card p-4">
      {showTitle && <h3 className="mb-3 text-base font-semibold text-brand-text-primary">Promote to Prospect</h3>}

      {/* Mode toggle */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setMode('pdf')}
          className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
            mode === 'pdf'
              ? 'bg-brand-accent text-brand-text-inverse border-brand-accent'
              : 'bg-white text-brand-text-secondary border-gray-300 hover:border-brand-accent'
          }`}
        >
          Upload PDF
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
            mode === 'url'
              ? 'bg-brand-accent text-brand-text-inverse border-brand-accent'
              : 'bg-white text-brand-text-secondary border-gray-300 hover:border-brand-accent'
          }`}
        >
          Paste Link
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {mode === 'pdf' ? (
          <div>
            <label
              htmlFor={`promote-file-${leadId}`}
              className="block text-xs text-brand-text-secondary mb-1"
            >
              Attach Proposal PDF
            </label>
            <input
              id={`promote-file-${leadId}`}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              aria-label="Attach proposal PDF"
              className="block w-full text-sm text-brand-text-primary file:mr-3 file:py-1 file:px-3
                         file:rounded file:border-0 file:bg-brand-accent file:text-brand-text-inverse
                         file:cursor-pointer cursor-pointer"
            />
            {fileError && (
              <p className="text-xs text-red-500 mt-1" role="alert">{fileError}</p>
            )}
            {file && (
              <p className="text-xs text-brand-text-secondary mt-1">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        ) : (
          <div>
            <label
              htmlFor={`promote-url-${leadId}`}
              className="block text-xs text-brand-text-secondary mb-1"
            >
              Proposal or Presentation URL
            </label>
            <input
              id={`promote-url-${leadId}`}
              type="url"
              value={linkUrl}
              onChange={e => { setLinkUrl(e.target.value); setUrlError(null); }}
              placeholder="https://docs.google.com/presentation/..."
              aria-label="Proposal URL"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none
                         focus:ring-2 focus:ring-brand-accent focus:border-brand-accent"
            />
            {urlError && (
              <p className="text-xs text-red-500 mt-1" role="alert">{urlError}</p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitDisabled}
          className="self-start px-4 py-2 text-sm font-medium rounded-md bg-brand-accent text-brand-text-inverse
                     hover:bg-brand-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2
                     focus:ring-brand-accent disabled:opacity-40 transition-colors"
          aria-busy={promote.isPending}
        >
          {promote.isPending ? 'Promoting…' : 'Promote to Prospect'}
        </button>

        {promote.isSuccess && (
          <p className="text-xs text-green-600" role="status">
            Lead promoted to Prospect successfully.
          </p>
        )}
      </form>
    </div>
  );
}
